import { useQuery } from '@tanstack/react-query'
import { ApiError } from '../../api'
import { POLL_MS, getAsOf, type AsOf } from './api'
import { noteRelayAsOf, noteRelayFailure, type RelayLive } from './live'

/**
 * A GET of the relay API through react-query: reports as_of (and failures) to
 * the live store for the freshness banner. Polls every 5 s while the SSE
 * stream is not open (`poll`), never retries a 4xx.
 */
export function useRelayGet<T>(key: readonly unknown[], path: string | null, live: RelayLive, opts: { poll?: boolean } = {}) {
  return useQuery<AsOf<T>>({
    queryKey: key,
    enabled: path !== null && live.available === true,
    queryFn: async () => {
      try {
        const r = await getAsOf<T>(path!)
        noteRelayAsOf(r.asOf, r.at)
        return r
      } catch (e) {
        noteRelayFailure(e)
        throw e
      }
    },
    retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 1,
    refetchInterval: opts.poll && live.sse !== 'open' ? POLL_MS : false,
  })
}

/** "GET /api/admin/relay/routes/r42: 502 Bad Gateway" for the error box. */
export function errorText(path: string, e: unknown): string {
  if (e instanceof ApiError) return `GET ${path}: ${e.status} ${e.message}`
  return `GET ${path}: ${(e as Error)?.message ?? 'failed'}`
}
